import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useLayoutEffect, useRef, useState, useEffect } from "react";

interface Props {
  text: string;
  tooltipId: string;
}

export const MiddleTruncatedTextWithTooltip = ({
  text,
  tooltipId,
}: Props) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [displayText, setDisplayText] = useState(text);
  const [isOverflow, setIsOverflow] = useState(false);

  const recalc = () => {
    if (!spanRef.current || !text) return;

    const td = spanRef.current.closest("td");
    if (!td) return;

    const spanRect = spanRef.current.getBoundingClientRect();
    const tdRect = td.getBoundingClientRect();

    const availableWidth = tdRect.right - spanRect.left - 4;
    if (availableWidth <= 0) return;

    const style = getComputedStyle(spanRef.current);
    const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    ctx.font = font;

    const measure = (value: string) => ctx.measureText(value).width;

    if (measure(text) <= availableWidth) {
      setDisplayText(text);
      setIsOverflow(false);
      return;
    }

    let left = 1;
    let right = Math.floor(text.length / 2);
    let best = text;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const candidate =
        text.slice(0, mid) + "..." + text.slice(text.length - mid);

      if (measure(candidate) <= availableWidth) {
        best = candidate;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    setDisplayText(best);
    setIsOverflow(true);
  };

  // initial + text change
  useLayoutEffect(recalc, [text]);

  // observe td size changes (monitor switch / resize)
  useEffect(() => {
    if (!spanRef.current) return;

    const td = spanRef.current.closest("td");
    if (!td) return;

    const observer = new ResizeObserver(() => {
      recalc();
    });

    observer.observe(td);

    return () => observer.disconnect();
  }, [text]);

  const content = (
    <span
      ref={spanRef}
      style={{
        display: "inline-block",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        cursor: "default",
      }}
    >
      {displayText}
    </span>
  );

  if (!isOverflow) return content;

  return (
    <OverlayTrigger
      placement="auto"
      delay={{ show: 200, hide: 10 }}
      overlay={<Tooltip id={tooltipId}>{text}</Tooltip>}
    >
      {content}
    </OverlayTrigger>
  );
};
